import React from 'react';
import { useAccountStore } from '../../components/Stores/accountStore';
import { DashBoardDonutCard, IDashboardDonutChartDataSet } from '../../components/UI-Elements/DashBoardDonutCard';
import { FutureOrdersSummeryBarChart } from '../FutureOrder/FutureOrders/FutureOrdersSummeryGraphs/FutureOrdersSummeryGraphsCharts/FutureOrdersSummeryBarChart';
import { FutureOrdersSummeryGraphsSkeleton } from '../FutureOrder/FutureOrders/FutureOrdersSummeryGraphs/FutureOrdersSummeryGraphsCharts/FutureOrdersSummeryGraphsSkeleton';
import { FutureOrdersSummaryLegend } from '../FutureOrder/FutureOrders/FutureOrdersSummeryGraphs/FutureOrdersSummeryGraphsCharts/FutureOrdersSummaryLegend';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { getCurrentMonthYear, MonthYear } from '../../utils/MonthYear/monthYear';

interface MaterialExposureDictionary {
  totalValue: number;
  totalWeight: number;
  exposureCalendar: Record<string, number>;
  price?: number;
}

interface Props {
  componentDataState: 'loading' | 'missing' | 'fetched';
  supplyAggregated: Record<string, MaterialExposureDictionary>;
}

export const SupplySummaryGraphs: React.FC<Props> = ({ componentDataState, supplyAggregated }) => {
  const { formulaColorDictionary: colorsMap, account } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  
  // Create a map from formula code to name for the legend
  const formulaCodeToNameMap = React.useMemo(() => {
    const map: Record<string, { name: string }> = {};
    account.formulasToAccount.forEach(formula => {
      map[formula.formulaId] = { name: formula.displayName || formula.formulaId };
    });
    return map;
  }, [account.formulasToAccount]);

  const getDonutData = (
    orders: Record<string, { totalValue: number; exposureCalendar: Record<string, number> }>,
    valueByCurrentMonth?: MonthYear,
  ): IDashboardDonutChartDataSet[] =>
    Object.entries(orders).map(([materialId, item]): IDashboardDonutChartDataSet => {
      const value = valueByCurrentMonth
        ? item.exposureCalendar[`${valueByCurrentMonth.year}${valueByCurrentMonth.month}`] || 0
        : item.totalValue;
      return {
        ratio: 0,
        fieldName: materialId,
        value,
        color: colorsMap[materialId],
      };
    });

  return (
    <div className="bg-white shadow-sm p-[1.5rem]">
      {componentDataState === 'loading' && <FutureOrdersSummeryGraphsSkeleton />}
      {(componentDataState === 'fetched' || componentDataState === 'missing') && (
        <div className="flex justify-between space-x-4 max-h-[15rem]">
          <div>
            <h5>Summary</h5>
            <div className="max-w-[8.75rem]">
              <DashBoardDonutCard
                secondaryTitle="Total value"
                dataSet={getDonutData(supplyAggregated)}
              />
            </div>
          </div>
          <div className="flex my-auto min-w-60 overflow-y-scroll max-h-[12rem]">
            <FutureOrdersSummaryLegend
              materialsDataAsDictionary={supplyAggregated}
              itemNames={formulaCodeToNameMap}
              colorsMap={colorsMap}
            />
          </div>
          <div className="flex flex-1">
            <FutureOrdersSummeryBarChart
              materialsDataAsDictionary={supplyAggregated}
              itemNames={formulaCodeToNameMap}
              colorsMap={colorsMap}
            />
          </div>
        </div>
      )}
    </div>
  );
};
